import { createContext, useState, useEffect } from "react";
import { getPrinterSettings } from "../utils/printerStorage";

export const UserContext = createContext();

const apiUrl = process.env.REACT_APP_API_URL;

// ─── localStorage helpers ─────────────────────────────────────────────────────

function loadCache(key, fallback) {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
    } catch {
        return fallback;
    }
}

function saveCache(key, value) {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // storage full or unavailable — fail silently
    }
}

function clearCache() {
    ["app_session", "app_categories", "app_payment_methods"].forEach((k) =>
        localStorage.removeItem(k)
    );
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export const UserProvider = ({ children }) => {

    const [loading, setLoading] = useState(true);

    // ✅ Rehydrate from localStorage immediately — no flicker on refresh
    const [session, setSession] = useState(() => loadCache("app_session", null));
    const [categories, setCategories] = useState(() => loadCache("app_categories", []));
    const [paymentMethods, setPaymentMethods] = useState(() => loadCache("app_payment_methods", []));
    const [printerNames, setPrinterNames] = useState([]);

    // ✅ Sync to localStorage whenever values change
    useEffect(() => { saveCache("app_session", session); }, [session]);
    useEffect(() => { saveCache("app_categories", categories); }, [categories]);
    useEffect(() => { saveCache("app_payment_methods", paymentMethods); }, [paymentMethods]);

    // ✅ Fetch fresh data on mount — updates cache in background
    useEffect(() => {
        const initializeApp = async () => {

            try {
                const [sessionResponse, settingsResponse] = await Promise.all([
                    fetch(`${apiUrl}sessions/check_session.php`, {
                        method: "GET",
                        credentials: "include",
                        headers: { "Content-Type": "application/json" },
                    }),
                    fetch(`${apiUrl}settings/get_settings.php`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                    }),
                ]);

                const sessionResult = await sessionResponse.json();
                const settingsResult = await settingsResponse.json();

                // If session is invalid, clear everything and stop
                // if (!sessionResult?.valid) {
                //     clearCache();
                //     setSession(null);
                //     setLoading(false);
                //     return;
                // }

                setSession(sessionResult);
                setCategories(settingsResult.categories || []);
                setPaymentMethods(settingsResult.payment_methods || []);

                const printers = getPrinterSettings();
                setPrinterNames(printers);

            } catch (error) {
                console.error("Initialization Error:", error);
                // Keep cached values on network failure — don't wipe them
            } finally {
                setLoading(false);
            }
        };

        initializeApp();
    }, []);

    // ✅ Expose a logout helper that wipes cache too
    const logout = () => {
        clearCache();
        setSession(null);
        setCategories([]);
        setPaymentMethods([]);
    };

    return (
        <UserContext.Provider
            value={{
                loading,

                session,
                setSession,

                categories,
                setCategories,

                paymentMethods,
                setPaymentMethods,

                printerNames,
                setPrinterNames,

                logout,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};




